import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { ArrowLeft, Check, Send } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import "./CreateBookingRequest.css";
import { Button } from "../../GloablComponents/Button";
import { CreateBookingRequestStore } from "./CreateBookingRequestStore";
import { AnalyticsSource } from "../../AnalyticsClient/AnalyticsSources";
import { AnalyticsClient } from "../../AnalyticsClient/AnalyticsClient";
import { LookstyLoader } from "../../GloablComponents/Loader/Loader";
import { DelayedLookstyLoader } from "../../GloablComponents/Loader/DelayedLookstyLoader";

export const CreateBookingRequestPage = observer(() => {
  const store = useMemo(() => new CreateBookingRequestStore(), []);
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || AnalyticsSource.Direct;

  useEffect(() => {
    const id = Number(params.id);
    if (id) store.load(id);
  }, [params.id, store]);

  useEffect(() => {
    const id = Number(params.id);

    if (!id) return;

    AnalyticsClient.track({
      eventType: "booking_request_started",
      source,
      entityType: "master",
      entityId: id,
    });
  }, [params.id, source]);

  if (store.IsLoading) {
    return (
      <main className="booking-request-page booking-request-page-loading">
        <DelayedLookstyLoader title="Загружаем заявку" subtitle="Готовим услуги мастера" show={store.IsLoading} />
      </main>
    );
  }

  return (
    <>
      {store.SuccessMessage && <div className="booking-request-toast">{store.SuccessMessage}</div>}

      {store.IsCreating && (
        <div className="booking-request-loader-overlay">
          <LookstyLoader title="Отправляем заявку" subtitle="Передаём данные мастеру" />
        </div>
      )}
      <main className="booking-request-page">
        <header className="booking-request-header">
          <button className="booking-request-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={22} />
          </button>

          <div>
            <span>Looksty</span>
            <h1>Заявка</h1>
          </div>
        </header>

        <section className="booking-request-intro">
          <h2>Оставить заявку</h2>
          <p>Мастер свяжется с вами и уточнит свободное время.</p>
        </section>

        {store.Error && <div className="booking-request-message error">{store.Error}</div>}

        <section className={store.FieldErrors.serviceId ? "booking-request-section has-error" : "booking-request-section"}>
          <h3>
            Услуга <em>*</em>
          </h3>

          <div className="booking-request-services">
            {store.Services.map((service) => (
              <button
                key={service.id}
                type="button"
                className={Number(store.ServiceId) === service.id ? "booking-request-service active" : "booking-request-service"}
                onClick={() => store.setServiceId(String(service.id))}
              >
                <div>
                  <strong>{service.name}</strong>
                  <span>
                    {service.price.toLocaleString("ru-RU")} ₽ · {service.durationMinutes} мин
                  </span>
                </div>

                {Number(store.ServiceId) === service.id && <Check size={19} />}
              </button>
            ))}
          </div>

          {store.FieldErrors.serviceId && <div className="booking-request-field-error">{store.FieldErrors.serviceId}</div>}

          {!store.Services.length && <div className="booking-request-empty">У мастера пока нет услуг</div>}
        </section>

        <section className="booking-request-section">
          <h3>Контакты</h3>

          <label className={store.FieldErrors.clientName ? "booking-request-field has-error" : "booking-request-field"}>
            <span>
              Имя <em>*</em>
            </span>
            <input value={store.ClientName} onChange={(e) => store.setClientName(e.target.value)} placeholder="Как к вам обращаться" aria-invalid={!!store.FieldErrors.clientName} />
            {store.FieldErrors.clientName && <div className="booking-request-field-error">{store.FieldErrors.clientName}</div>}
          </label>

          <label className={store.FieldErrors.contact ? "booking-request-field has-error" : "booking-request-field"}>
            <span>
              Telegram / телефон <em>*</em>
            </span>
            <input value={store.Contact} onChange={(e) => store.setContact(e.target.value)} placeholder="@username или номер телефона" aria-invalid={!!store.FieldErrors.contact} />
            {store.FieldErrors.contact && <div className="booking-request-field-error">{store.FieldErrors.contact}</div>}
          </label>

          <label className={store.FieldErrors.preferredTime ? "booking-request-field has-error" : "booking-request-field"}>
            <span>
              Когда удобно <em>*</em>
            </span>
            <input value={store.PreferredTime} onChange={(e) => store.setPreferredTime(e.target.value)} placeholder="Например: завтра после 18:00" aria-invalid={!!store.FieldErrors.preferredTime} />
            {store.FieldErrors.preferredTime && <div className="booking-request-field-error">{store.FieldErrors.preferredTime}</div>}
          </label>

          <label className="booking-request-field">
            <span>Комментарий</span>
            <textarea value={store.Comment} onChange={(e) => store.setComment(e.target.value)} placeholder="Пожелания или детали" />
          </label>
        </section>

        <section className="booking-request-submit">
          <Button
            disabled={store.IsCreating}
            onClick={() =>
              store.create(() => {
                setTimeout(() => navigate(-1), 1200);
              })
            }
          >
            <Send size={18} />
            {store.IsCreating ? "Отправляем..." : "Отправить заявку"}
          </Button>
        </section>
      </main>
    </>
  );
});
